import { FlowComponent } from '../../flow.min.js';
import styles from './styles.js';
import { sessionChannel, contextChannel } from '../../stores/index.js';
import { api } from '../../services/api.js';

import '../ui/flow-loading/flow-loading.js';
import '../ui/flow-banner/flow-banner.js';
import '../ui/flow-feature-lock/flow-feature-lock.js';

const template = FlowComponent.html`
    <div id="app-wrapper" class="flex flex-col min-h-screen">
        <div id="global-banners" class="w-full"></div>
        <div id="view-container" class="flex-1 flex flex-col">
            <slot></slot>
        </div>
    </div>
`;

const VIEW_MAP = new Map([
    ['login', async () => {
        await import('../auth-login/auth-login.js');
        return document.createElement('auth-login');
    }],
    ['dashboard', async () => {
        await import('../layout/panel-layout/panel-layout.js');
        return document.createElement('panel-layout');
    }],
    ['wizard', async () => {
        await import('../views/wizard/first-time-wizard.js');
        return document.createElement('first-time-wizard');
    }],
    ['locked', async (data) => {
        await import('../ui/flow-subscription-lock/flow-subscription-lock.js');
        const lock = document.createElement('flow-subscription-lock');
        lock.setAttribute('status', data?.status || 'canceled');
        return lock;
    }],
    ['loading', async (data) => {
        const loading = document.createElement('flow-loading');
        if (data?.message) loading.setAttribute('message', data.message);
        return loading;
    }]
]);

export class AppRoot extends FlowComponent {
    #currentView = null;
    #isBooting = true;

    static get styles() { return styles; }
    static get template() { return template; }

    onMount() {
        super.onMount();
        const hasToken = !!sessionStorage.getItem('authToken') || !!localStorage.getItem('authToken');
        
        if (hasToken) {
            this.#navigate('loading', { message: 'Iniciando sesión...' });
        }

        this.#initSession();

        sessionChannel.bind(this, (el, session) => {
            if (this.#isBooting && hasToken) return;
            if (!session?.isAuthenticated && this.#currentView !== 'login') {
                this.#navigate('login');
            } else if (session?.isAuthenticated && !session.subscription && this.#currentView === 'login') {
                this.#initSession();
            }
        });
    }

    async #initSession() {
        const token = sessionStorage.getItem('authToken') || localStorage.getItem('authToken');
        const jwtRegex = /^eyJ[A-Za-z0-9_-]+\.eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;

        if (token && jwtRegex.test(token)) {
            if (this.#currentView !== 'loading') {
                this.#navigate('loading', { message: 'Verificando cuenta...' });
            }

            try {
                const response = await api.request('/api/v1/subscriptions', {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                const { user, subscription } = response.data;
                const status = subscription?.billing?.status || 'active';

                // Primero actualizamos el canal (Helium guardará el estado)
                sessionChannel.send({
                    user,
                    token,
                    isAuthenticated: true,
                    subscription
                });

                // Auto-restore Branch Context if available & permitted
                const storedBranchId = localStorage.getItem('_b');
                if (storedBranchId) {
                    const hasAccess = user.isAccountManager ||
                        user.memberships?.some(m => m.branchId === storedBranchId);
                    
                    if (hasAccess) {
                        contextChannel.send({ branchId: storedBranchId });
                    } else {
                        localStorage.removeItem('_b'); // Clean up invalid context
                    }
                }

                // Luego navegamos y quitamos el modo arranque
                this.#isBooting = false;
                this.#handleAccess(user, status);

            } catch (error) {
                console.error('⛔ Error en sesión:', error);
                this.#isBooting = false;
                this.#logout();
            }
        } else {
            this.#isBooting = false;
            this.#navigate('login');
        }

        // Escuchamos cuando el wizard termina para refrescar el acceso
        this.addEventListener('wizard-finished', () => {
            const session = sessionChannel.value;
            if (session?.user) {
                session.user.wizardProgress = { ...session.user.wizardProgress, completed: true };
                sessionChannel.send(session); // Actualizamos Helium
                this.#handleAccess(session.user, session.subscription?.billing?.status || 'active');
            }
        });
    }

    #handleAccess(user, status) {
        if (!user?.wizardProgress?.completed) {
            this.#navigate('wizard');
            return;
        }

        const accessMap = {
            active: 'dashboard',
            trial: 'dashboard',
            past_due: 'dashboard'
        };
        this.#navigate(accessMap[status] || 'locked', { status });
    }

    #logout() {
        sessionStorage.clear();
        localStorage.removeItem('authToken');
        localStorage.removeItem('_b');
        
        sessionChannel.send({
            user: null,
            token: null,
            isAuthenticated: false,
            subscription: null
        });

        contextChannel.send({
            branchId: null
        });

        this.#navigate('login');
    }

    async #navigate(view, data = null) {
        if (this.#currentView === view && view !== 'locked') return;
        const loader = VIEW_MAP.get(view);
        if (!loader) return;

        this.#currentView = view;
        this.innerHTML = '';

        try {
            const component = await loader(data);
            this.appendChild(component);
        } catch (error) {
            console.error(`❌ Error en vista [${view}]:`, error);
        }
    }
}

customElements.define('app-root', AppRoot);
